import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isAdminSelector } from '../../../components/store/auth/selectors';
import { useAccessToken } from '../../../components/common/hooks/useAccessToken';
import { showLoader, hideLoader } from '../../../components/store/common/slice';
import Table from '../../../components/common/table/TableFunctional';
import BllinkUtils from '../../../components/common/utils/utils';
import autoCampaignActions from '../../../components/common/simple/autoCampaignActions';
import PrintPdfIcons from '../../../components/common/export_documents/PrintPdfIcons';
import Search from '../../../components/images/search.svg';
import { StyledSection } from './AutoCampaignStatus.styled';
import PartnersAndBuildingsDropdowns from '../../components/PartnersAndBuildingsDropdowns/PartnersAndBuildingsDropdowns';

const AutoCampaignStatus = () => {
    const isAdmin = useSelector(isAdminSelector);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();

    const [filters, setFilters] = useState({});
    const [currentPartner, setCurrentPartner] = useState([]);
    const [currentBuildings, setCurrentBuildings] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [disabledGetBtn, setDisabledGetBtn] = useState(true);
    const pdfContainer = useRef();

    useEffect(() => {
        if (currentPartner.length === 0) setDisabledGetBtn(true);
        else if (currentPartner.length === 1) setDisabledGetBtn(false);
        else setDisabledGetBtn(false);
    }, [currentPartner])

    const onPartnerChange = e => setCurrentPartner(e);

    const onBuildingsChange = e => setCurrentBuildings(e);

    const createPayloadArr = arr => {
        let options = [];
        arr.map(item => options.push(item.value))
        return options
    };

    const onGetBtnClick = async () => {
        dispatch(showLoader());
        let result;
        if (currentPartner.length === 1 && currentBuildings.length === 0) {
            const payload = {
                partnerID: currentPartner[0].value
            };
            result = await client.get('/operations/auto-campaigns/status/partner', payload);
            setTableData(result.status);
        } else if (currentPartner.length === 1 && currentBuildings.length > 0) {
            const payload = {
                BuildingId: createPayloadArr(currentBuildings),
            };
            result = await client.get('/operations/auto-campaigns/status/buildings', payload)
            setTableData(result.status);
        } else {
            const payload = {
                partnerID: createPayloadArr(currentPartner)
            };
            result = await client.get('/operations/auto-campaigns/status/partner', payload)
            setTableData(result.status);
        }
        dispatch(hideLoader());
    };

    const columns = [
        {
            Header: t('autoCampaign.status.tableHeaders.partnerName'),
            id: 'partnerName',
            accessor: 'AutoCampaign.Building.ManagementCompany.PartnerDetails.partnerName',
        },
        {
            Header: t('autoCampaign.status.tableHeaders.aptNumber'),
            id: 'aptNumber',
            accessor: 'Apartment.ApartmentNum',
            Cell: ({ row }) => {
                const orig = row.original;
                return (
                    <Link to={`/reports/apartment/${orig.AutoCampaign.Building.externalID}/${orig.Apartment.ApartmentNum}`} target='_blank'>
                        {orig.Apartment.ApartmentNum}
                    </Link>
                )
            }
        },
        {
            Header: t('autoCampaign.status.tableHeaders.buildingTitle'),
            id: 'buildingTitle',
            accessor: 'AutoCampaign.Building.Title',
            Cell: ({ row }) => {
                const orig = row.original;
                return (
                    <Link to={`/reports/yearly-by-months/${orig.AutoCampaign.Building.externalID}/2021`} target='_blank'>
                        {orig.AutoCampaign.Building.Title}
                    </Link>
                )
            }
        },
        {
            Header: t('autoCampaign.status.tableHeaders.step'),
            id: 'step',
            accessor: 'FlowStep.AutoCampaignAction.name',
            Cell: ({ row }) => {
                const orig = row.original;
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={props => (
                            <Tooltip {...props}>
                                {autoCampaignActions.map(({ id, key }, i) => {
                                    return (
                                        <div key={i}>
                                            {id} - {t(`autoCampaign.status.actions.${key}`)}
                                        </div>
                                    )
                                })}
                            </Tooltip>
                        )}
                    >
                        <span>
                            {orig.FlowStepId} - {orig.FlowStep.AutoCampaignAction.name}
                        </span>
                    </OverlayTrigger>
                )
            }
        },
        {
            Header: t('autoCampaign.status.tableHeaders.stepSuccess'),
            id: 'stepSuccess',
            accessor: 'successful',
            Cell: ({ row }) => {
                const orig = row.original;
                return (
                    <>
                        {orig.successful ? t('autoCampaign.status.yes') : (
                            <OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('autoCampaign.status.failedStep')} 
                                    </Tooltip>
                                )}
                            >
                                <span>
                                    {t('autoCampaign.status.no')}
                                </span>
                            </OverlayTrigger>
                        )}
                    </>
                )
            }
        },
        {
            Header: t('autoCampaign.status.tableHeaders.lastAction'),
            id: 'lastDate',
            accessor: 'lastDate',
            Cell: ({ row }) => {
                const dateParsed = BllinkUtils.BllinkTime.parseDate(row.original.lastDate);
                return <>{dateParsed} {new Date(row.original.lastDate).toLocaleTimeString()}</>
            }
        },
        {
            Header: t('autoCampaign.status.tableHeaders.campaignName'),
            id: 'campaignName',
            accessor: 'AutoCampaign.Flow.name',
        },
        {
            Header: t('autoCampaign.status.tableHeaders.campaignFinished'),
            id: 'campaignFinished',
            accessor: 'AutoCampaign.finished',
            Cell: ({ row }) => {
                const orig = row.original;
                return (
                    <>
                        {orig.AutoCampaign.finished ? t('autoCampaign.status.yes') : t('autoCampaign.status.no')}
                    </>
                )
            }
        }
    ];

    if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    return (
        <StyledSection>
            <h4>{t('autoCampaign.status.subtitle')}</h4>
            <Container>
                <Row>
                    <Col sm={3}>
                        <div className="control-with-icon search">
                            <input
                                type="text"
                                className="form-control bllink-input-border"
                                name="search"
                                onChange={e => setFilters({
                                    global: e.target.value
                                })}
                            />
                            <img src={Search} alt={t('search.search')} />
                        </div>
                    </Col>
                    <Col sm={6} className='dropdowns-wrapper'>
                        <PartnersAndBuildingsDropdowns
                            onPartnerChange={onPartnerChange}
                            onBuildingsChange={onBuildingsChange}
                        />
                    </Col>
                    <Col sm={2}>
                    <button
                        className='bllink-button'
                        onClick={onGetBtnClick}
                        disabled={disabledGetBtn}
                    >
                        {t('autoCampaign.status.getBtn')}
                    </button>
                    </Col>
                </Row>
            </Container>

            <Row className='icons-wrapper'>
                <PrintPdfIcons
                    pdfContainer={pdfContainer.current}
                    eventLabel={t('autoCampaign.status.exportTitle')}
                    titleString={t('autoCampaign.status.exportTitle')}
                    excelTitle={t('autoCampaign.status.exportTitle')}
                    reportName={t('autoCampaign.status.title')}
                />
            </Row>
            <Container id="section-to-print" ref={pdfContainer}>
                {tableData && tableData.length > 0 && (
                        <Table
                            columns={columns}
                            data={tableData}
                            filters={filters}
                            initialSorting={[{
                                id: 'lastDate',
                                desc: true
                            }]}
                        />
                    )
                }
                {
                    (!tableData || tableData.length === 0) && <div className='nothing-found'>{t('searchPayments.nothingFound')}</div>
                }
            </Container>
        </StyledSection>
    )
};

export default AutoCampaignStatus;
